import React,{useEffect,useId,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {FiMoreHorizontal,FiRefreshCw,FiFileText,FiEdit2,FiTrash2} from 'react-icons/fi';

/** One accessible, contextual action button per interview card.
 *  The menu lives in a portal so the scrollable Files sidebar cannot clip it.
 */
export default function FileActionsMenu({row,name,disabled,canTranscribe,transcribing,onTranscribe,onRename,onDelete}){
 const [open,setOpen]=useState(false);
 const [position,setPosition]=useState({top:0,left:0});
 const trigger=useRef(null),menu=useRef(null);
 const menuId=useId();
 const hasTranscript=Boolean(row.data?.words?.length);
 const fileLabel=hasTranscript?'Re-transcribe':'Transcribe';
 const close=({focus=false}={})=>{setOpen(false);if(focus)trigger.current?.focus();};
 const toggle=()=>{
  if(disabled)return;
  if(open){close();return;}
  const rect=trigger.current?.getBoundingClientRect();
  if(!rect)return;
  const width=218,height=155,pad=8;
  const left=Math.max(pad,Math.min(window.innerWidth-width-pad,rect.right-width));
  const above=rect.top-height-6;
  const below=rect.bottom+6;
  const top=below+height<=window.innerHeight-pad ? below
   :above>=pad ? above
   :Math.max(pad,Math.min(window.innerHeight-height-pad,below));
  setPosition({left,top});
  setOpen(true);
 };
 useEffect(()=>{
  if(!open)return;
  const outside=event=>{
   if(!menu.current?.contains(event.target)&&!trigger.current?.contains(event.target))close();
  };
  const escape=event=>{
   if(event.key==='Escape'){event.preventDefault();event.stopPropagation();close({focus:true});}
  };
  const scroll=()=>close();
  document.addEventListener('pointerdown',outside,true);
  document.addEventListener('keydown',escape,true);
  document.addEventListener('scroll',scroll,true);
  window.addEventListener('resize',scroll);
  const frame=requestAnimationFrame(()=>menu.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus());
  return()=>{
   cancelAnimationFrame(frame);
   document.removeEventListener('pointerdown',outside,true);
   document.removeEventListener('keydown',escape,true);
   document.removeEventListener('scroll',scroll,true);
   window.removeEventListener('resize',scroll);
  };
 },[open]);
 useEffect(()=>{if(disabled&&open)setOpen(false);},[disabled,open]);
 const act=action=>{close();action();};
 const navigate=event=>{
  if(!['ArrowDown','ArrowUp','Home','End'].includes(event.key))return;
  const options=[...menu.current?.querySelectorAll('[role="menuitem"]:not(:disabled)')||[]];
  if(!options.length)return;
  event.preventDefault();
  const index=options.indexOf(document.activeElement);
  let next=0;
  if(event.key==='End')next=options.length-1;
  else if(event.key==='ArrowDown')next=(index+1)%options.length;
  else if(event.key==='ArrowUp')next=(index-1+options.length)%options.length;
  options[next]?.focus();
 };
 return <>
  <button ref={trigger} type="button" className="file-tile-more-trigger"
    aria-label={`More actions for ${name}`} aria-haspopup="menu"
    aria-expanded={open} aria-controls={open?menuId:undefined}
    title={`More actions for ${name}`} disabled={disabled} onClick={toggle}>
   <FiMoreHorizontal aria-hidden="true"/>
  </button>
  {open&&createPortal(
   <div ref={menu} id={menuId} className="file-tile-popover" role="menu"
     aria-label={`Actions for ${name}`}
     style={{top:position.top,left:position.left}} onKeyDown={navigate}>
    <button type="button" role="menuitem"
      disabled={!canTranscribe}
      onClick={()=>act(()=>onTranscribe(row.id))}>
     {hasTranscript?<FiRefreshCw aria-hidden="true"/>:<FiFileText aria-hidden="true"/>}
     <span>{transcribing?'Transcribing…':fileLabel}</span>
    </button>
    <button type="button" role="menuitem" onClick={()=>act(()=>onRename(row))}>
     <FiEdit2 aria-hidden="true"/><span>Rename interview</span>
    </button>
    <div className="file-tile-popover-divider" role="separator"/>
    <button type="button" role="menuitem" className="file-tile-popover-danger"
      onClick={()=>act(()=>onDelete(row))}>
     <FiTrash2 aria-hidden="true"/>
     <span>Remove interview</span>
    </button>
   </div>,document.body)}
 </>;
}
